import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { removeResumeState } from '../lib/activityIntelligence';
import {
  activeHeadingAnchor,
  buildArticleEntity,
  findArticleResume,
  normalizeReadingSample,
  persistReadingSample,
  restoreReadingPosition,
} from '../lib/readingProgress';
import {
  RESUME_NOTICE_ACTION_EVENT,
  emitResumeNotice,
} from '../lib/resumeNotice';

const CAPTURE_INTERVAL_MS = 15000;
const ARTICLE_SELECTOR = '[data-testid="article-content-body"]';

const articleImage = () => document.querySelector('[data-testid="article-hero"] img')?.getAttribute('src') || undefined;
const articleAuthor = () => document.querySelector('[data-testid="author-info"] p.font-bold')?.textContent?.trim() || undefined;

const GlobalReadingProgressCapture = () => {
  const location = useLocation();
  const restoredKeyRef = useRef(null);

  useEffect(() => {
    const entity = buildArticleEntity({
      pathname: location.pathname,
      title: document.querySelector('[data-testid="article-title"]')?.textContent?.trim() || document.title,
      image: articleImage(),
      author: articleAuthor(),
    });
    if (!entity) return undefined;

    let articleElement = null;
    let intervalId = null;
    let retryId = null;
    let cancelled = false;

    const sample = () => {
      if (!articleElement) return null;
      const rect = articleElement.getBoundingClientRect();
      const articleTop = rect.top + window.scrollY;
      const reading = normalizeReadingSample({
        articleTop,
        articleHeight: articleElement.scrollHeight || rect.height,
        scrollY: window.scrollY,
        viewportHeight: window.innerHeight,
      });
      const readingLine = window.scrollY + window.innerHeight * 0.65;
      return persistReadingSample({
        entity,
        sample: reading,
        anchor: activeHeadingAnchor(articleElement, readingLine),
        metadata: {
          source: 'article-reader',
          route: location.pathname,
          image: entity.media?.image,
          author: entity.author,
        },
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sample();
    };

    const handleResumeAction = (event) => {
      const detail = event.detail;
      const key = `${entity.type}:${entity.id}`;
      if (detail?.action !== 'start-over' || detail?.notice?.id !== key) return;
      removeResumeState(entity);
      const rect = articleElement?.getBoundingClientRect();
      const top = rect ? Math.max(0, rect.top + window.scrollY - 96) : 0;
      window.scrollTo({ top, behavior: 'auto' });
      restoredKeyRef.current = key;
    };

    const initialise = (attempt = 0) => {
      if (cancelled) return;
      articleElement = document.querySelector(ARTICLE_SELECTOR);
      const title = document.querySelector('[data-testid="article-title"]')?.textContent?.trim();
      if (!articleElement || !title) {
        if (attempt < 30) retryId = window.setTimeout(() => initialise(attempt + 1), 200);
        return;
      }

      entity.title = title;
      entity.media = articleImage() ? { image: articleImage() } : entity.media;
      entity.author = articleAuthor() || entity.author;

      const restoreKey = `${entity.type}:${entity.id}`;
      if (restoredKeyRef.current !== restoreKey && !location.hash) {
        const saved = findArticleResume(entity);
        const rect = articleElement.getBoundingClientRect();
        const target = restoreReadingPosition({
          item: saved,
          articleTop: rect.top + window.scrollY,
          articleHeight: articleElement.scrollHeight || rect.height,
        });
        let restoredAnchor = null;
        if (target !== null && saved?.metadata?.anchor) {
          const anchor = document.getElementById(saved.metadata.anchor);
          if (anchor) {
            restoredAnchor = anchor;
            window.scrollTo({ top: Math.max(0, anchor.getBoundingClientRect().top + window.scrollY - 96), behavior: 'auto' });
          } else {
            window.scrollTo({ top: target, behavior: 'auto' });
          }
        } else if (target !== null) {
          window.scrollTo({ top: target, behavior: 'auto' });
        }
        if (target !== null && saved) {
          if (restoredAnchor) {
            restoredAnchor.classList.add('ring-1', 'ring-primary/60');
            window.setTimeout(() => restoredAnchor?.classList.remove('ring-1', 'ring-primary/60'), 1800);
          }
          emitResumeNotice({
            entity,
            position: saved.position_seconds,
            unit: saved.metadata?.unit || 'pixels',
            anchor: saved.metadata?.anchor,
            kind: 'reading',
          });
        }
        restoredKeyRef.current = restoreKey;
      }

      intervalId = window.setInterval(sample, CAPTURE_INTERVAL_MS);
      window.addEventListener('pagehide', sample);
      window.addEventListener(RESUME_NOTICE_ACTION_EVENT, handleResumeAction);
      document.addEventListener('visibilitychange', onVisibilityChange);
    };

    initialise();

    return () => {
      cancelled = true;
      if (retryId) window.clearTimeout(retryId);
      if (intervalId) window.clearInterval(intervalId);
      sample();
      window.removeEventListener('pagehide', sample);
      window.removeEventListener(RESUME_NOTICE_ACTION_EVENT, handleResumeAction);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [location.pathname, location.hash]);

  return null;
};

export default GlobalReadingProgressCapture;
